import { useId, type ReactNode } from 'react';

import { cx } from '../lib/cx';

export interface FormFieldRenderProps {
  id: string;
  'aria-describedby'?: string;
  'aria-invalid'?: true;
}

export interface FormFieldProps {
  label: string;
  /** Optional helper text below the label. */
  hint?: string;
  /** Inline validation message; presence flips the control to invalid. */
  error?: string;
  /** Visually hide the label but keep it for assistive tech. */
  hideLabel?: boolean;
  /** Render-prop: receives the wired id + aria attributes for the control. */
  children: (props: FormFieldRenderProps) => ReactNode;
  className?: string;
}

/**
 * A labelled form field with inline validation. Wires `label[for]`,
 * `aria-describedby` (hint and/or error) and `aria-invalid` onto the control via
 * a render prop, so any input primitive gets correct semantics without repeating
 * the plumbing.
 */
export function FormField({
  label,
  hint,
  error,
  hideLabel = false,
  children,
  className,
}: FormFieldProps): React.JSX.Element {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy = cx(hint !== undefined && hintId, error !== undefined && errorId) || undefined;

  const rendered: FormFieldRenderProps = {
    id,
    ...(describedBy === undefined ? {} : { 'aria-describedby': describedBy }),
    ...(error !== undefined ? { 'aria-invalid': true as const } : {}),
  };

  return (
    <div className={cx('ds-field', className)}>
      <label className={hideLabel ? 'sr-only' : 'ds-field__label'} htmlFor={id}>
        {label}
      </label>
      {hint !== undefined ? (
        <span id={hintId} className="ds-field__hint">
          {hint}
        </span>
      ) : null}
      {children(rendered)}
      {error !== undefined ? (
        <span id={errorId} className="ds-field__error" role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
}
